import React from "react";

import DatePicker from "react-datepicker";

import { range, capitalize } from "lodash";
import { getYear, getMonth } from "date-fns";

import Icon from "../../icon";

import "./datePicker.scss";

const weekDays = {
  Monday: new Date(1970, 5, 1),
  Tuesday: new Date(1970, 5, 2),
  Wednesday: new Date(1970, 5, 3),
  Thursday: new Date(1970, 5, 4),
  Friday: new Date(1970, 5, 5),
  Saturday: new Date(1970, 5, 6),
  Sunday: new Date(1970, 5, 7),
};

// TODO Add missing typos (all of them :)))
const DatePickerComponent = ({
  id,
  label,
  dateFormat = "yyyy/MM/dd",
  value,
  onChange,
  includeDateIntervals = null,
  openToDate,
  locale = "en-US",
  showCalendarIcon = false,
  hideLabel = false,
  startOpen = false,
  years = undefined,
  unstyledInput = false,
  customInput = <CustomInput showCalendarIcon={showCalendarIcon} id={id} label={label} hideLabel={hideLabel} unstyledInput={unstyledInput} />,
}) => {
  const dayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });

  const checkForWeekDay = (date) => {
    let isValidDay = true;
    if (includeDateIntervals) {
      includeDateIntervals.forEach(interval => {
        if (interval.weekDays) {
          const day = date.getDay();
          isValidDay = interval.weekDays[day];
        }
      })
    }

    return isValidDay;
  }

  return (
    <DatePicker
      filterDate={checkForWeekDay}
      startOpen={startOpen}
      openToDate={openToDate}
      formatWeekDay={(nameOfDay) => {
        if (weekDays[nameOfDay]) return capitalize(dayFormatter.format(weekDays[nameOfDay])).replace('.', '');
        return capitalize(nameOfDay.substring(0, 3));
      }}
      calendarStartDay={1}
      selected={value}
      onChange={(date) => onChange(date)}
      dateFormat={dateFormat}
      popperModifiers={[
        {
          name: "arrow",
          options: {
            padding: ({ popper }) => popper.width / 2,
          },
        },
      ]}
      calendarClassName={"react-datepicker-custom H400Medium"}
      customInput={customInput}
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <CustomHeader
          date={date}
          changeYear={changeYear}
          changeMonth={changeMonth}
          decreaseMonth={decreaseMonth}
          increaseMonth={increaseMonth}
          prevMonthButtonDisabled={prevMonthButtonDisabled}
          nextMonthButtonDisabled={nextMonthButtonDisabled}
          locale={locale}
          years={years}
        />
      )}
      locale={locale}
      disabledKeyboardNavigation
      includeDateIntervals={includeDateIntervals}
    />
  );
};

const CustomHeader = (props) => {
  const {
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    locale,
    years = range(getYear(new Date()) - 99, getYear(new Date()) + 2, 1)
  } = props;
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });

  const months = [
    monthFormatter.format(new Date(2000, 0)),
    monthFormatter.format(new Date(2000, 1)),
    monthFormatter.format(new Date(2000, 2)),
    monthFormatter.format(new Date(2000, 3)),
    monthFormatter.format(new Date(2000, 4)),
    monthFormatter.format(new Date(2000, 5)),
    monthFormatter.format(new Date(2000, 6)),
    monthFormatter.format(new Date(2000, 7)),
    monthFormatter.format(new Date(2000, 8)),
    monthFormatter.format(new Date(2000, 9)),
    monthFormatter.format(new Date(2000, 10)),
    monthFormatter.format(new Date(2000, 11)),
  ];
  // TODO: Reemplazar las chevrons por iconos
  return (
    <div className={"react-datepicker-custom__header"}>
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className={"react-datepicker-custom_chevron-button"}
      >
        {"<"}
      </button>

      <div className={"react-datepicker-custom__selectors"}>
        <select
          //   className={"H400Medium"}
          value={months[getMonth(date)]}
          onChange={({ target: { value } }) =>
            changeMonth(months.indexOf(value))
          }
        >
          {months.map((option) => (
            <option key={option} value={option}>
              {capitalize(option)}
            </option>
          ))}
        </select>

        <select
          //   className={"H400Medium"}
          value={getYear(date)}
          onChange={({ target: { value } }) => changeYear(value)}
        >
          {years.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          increaseMonth();
        }}
        disabled={nextMonthButtonDisabled}
        className={"react-datepicker-custom_chevron-button"}
      >
        {">"}
      </button>
    </div>
  );
};

const CustomInput = React.forwardRef(({ label, id, value, onClick, showCalendarIcon, hideLabel = true, unstyledInput = false }, ref) => (
  <div
    ref={ref}
    className="relative"
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.nativeEvent.code === "Space" || e.nativeEvent.code === "Enter")
        onClick();
    }}
    tabIndex={0}
  >
    <input
      onFocus={e => e.target.blur()}
      type="text"
      id={id}
      className={`${hideLabel ? "p-2" : "px-2.5 pb-2.5 pt-4"} relative block w-full text-base font-semibold text-[#242426] bg-transparent rounded-lg appearance-none focus:outline-none focus:ring-0 focus:border-[#3560DA] peer ${unstyledInput ? "border-none" : "border-1 border-[#efeff4]"}`}
      placeholder={hideLabel ? label : ' '}
      value={value}
      readOnly
    />
    {
      !hideLabel &&
      <label
        htmlFor={id}
        className="absolute text-[#75757D] text-base font-semibold duration-300 transform -translate-y-4 scale-75 top-2 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-[#3560DA] peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
      >
        {label}
      </label>
    }
    {showCalendarIcon && <Icon className={`w-5 absolute right-2 ${hideLabel ? "top-[12px]" : "top-[18px]"} `} icon="Calendar" />}
  </div>
));

export default DatePickerComponent;
